import styled from 'styled-components';
import type { PolyusLetterInfo } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { ToneBadge } from './tone';

/**
 * Шифры проектной корреспонденции ПОЛЮС в карточке письма диспетчера:
 * расшифровка (проект, дисциплина, отправитель, номер), отметка о выдаче
 * журналом и замечания к исходящему письму Заказчику. Для письма Заказчика —
 * его регистрационный номер («ПСЛ-2100 от 20.10.2026») и наши письма, на
 * которые оно отвечает (шифр или «Исх. №» найдены в теме, тексте или на бланке).
 */

function dateRu(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString('ru-RU') : '';
}

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Code = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const Line = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Issues = styled.ul`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(7)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.warning}15;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};

  li + li {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`;

const IssuesTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export function PolyusLetterBlock({
  info,
  onOpenLetter,
}: {
  info: PolyusLetterInfo;
  /** Открыть наше письмо, на которое ответил Заказчик (id в реестре корреспонденции). */
  onOpenLetter?: (correspondenceId: string) => void;
}) {
  const replies = info.replies_to ?? [];
  return (
    <Box>
      {info.customer_letter && (
        <Row>
          <Head>
            <Code>{info.customer_letter.reg_no}</Code>
            <ToneBadge $tone="info">Письмо Заказчика</ToneBadge>
          </Head>
          <Line>
            Регистрационный номер ООО «Полюс Сухой Лог»
            {info.customer_letter.reg_date
              ? ` от ${dateRu(info.customer_letter.reg_date)}`
              : ''}
            . В ответе Заказчику укажите его в поле «На №».
          </Line>
        </Row>
      )}
      {replies.map((r) => (
        <Row key={r.journal_id}>
          <Head>
            <IssuesTitle>Ответ на наше письмо</IssuesTitle>
            <Code>{r.code}</Code>
            <ToneBadge
              $tone="neutral"
              title={
                r.matched_by === 'code'
                  ? 'Заказчик указал шифр нашего письма'
                  : 'Заказчик указал наш исходящий номер'
              }
            >
              {r.matched_by === 'code' ? 'по шифру' : 'по исх. №'}
            </ToneBadge>
            {r.correspondence_id && onOpenLetter && (
              <Button
                type="button"
                $size="sm"
                $variant="secondary"
                onClick={() => onOpenLetter(r.correspondence_id as string)}
              >
                Наше письмо
              </Button>
            )}
          </Head>
          <Line>
            {r.subject}
            {r.outgoing_number ? ` · исх. № ${r.outgoing_number}` : ''}
            {r.sent_at ? ` · отправлено ${dateRu(r.sent_at)}` : ''}
          </Line>
        </Row>
      ))}
      {info.codes.map((c) => (
        <Row key={c.code}>
          <Head>
            <Code>{c.code}</Code>
            <ToneBadge $tone={c.from_customer ? 'info' : 'neutral'}>
              {c.from_customer ? 'Письмо Заказчика' : `Письмо ${c.sender}`}
            </ToneBadge>
            {c.on_blank && (
              <ToneBadge $tone="neutral" title="Шифр найден на бланке письма во вложении">
                С бланка
              </ToneBadge>
            )}
            {c.journal && (
              <ToneBadge
                $tone={c.journal.status === 'cancelled' ? 'warning' : 'success'}
                title={c.journal.subject}
              >
                {c.journal.status === 'cancelled'
                  ? 'Аннулирован в журнале'
                  : c.journal.status === 'sent'
                    ? 'В журнале: отправлено'
                    : 'Выдан журналом'}
              </ToneBadge>
            )}
          </Head>
          <Line>
            Проект: {c.project}
            {c.project_name ? ` — ${c.project_name}` : ''} · Дисциплина: {c.discipline}
            {c.discipline_name ? ` — ${c.discipline_name}` : ''} · №{' '}
            {String(c.seq).padStart(5, '0')}
          </Line>
          {c.problems.length > 0 && (
            <Issues>
              {c.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </Issues>
          )}
        </Row>
      ))}
      {info.issues.length > 0 && (
        <Row>
          <IssuesTitle>Замечания по правилам кодирования ПОЛЮС</IssuesTitle>
          <Issues>
            {info.issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </Issues>
        </Row>
      )}
      {info.outgoing_to_customer && info.issues.length === 0 && (
        <Line>Письмо Заказчику оформлено по правилам кодирования.</Line>
      )}
    </Box>
  );
}
